/**
 * 다음 회기 Brief — 규칙 기반 자동 생성 (MASTER §14, CLINICAL_DATA_MODEL §13).
 * AI 없이 이미 저장·승인된 구조화 자료만 재조합한다. 새로운 사실을 만들지 않는다.
 * 가설은 "확인 중인 가설"로만 옮기고, 사실과 섞지 않는다.
 */
import type {
  CaseFormulationRevision,
  CounselingGoal,
  CounselingJournal,
  NextSessionBrief,
  RiskAssessment,
  Session,
  SessionAnalysis,
} from '../schema/v1';
import { RISK_KEYS, type RiskKey } from '../schema/v1';

export const RISK_LABELS: Record<RiskKey, string> = {
  suicidalIdeation: '자살사고',
  suicidalPlan: '자살계획',
  suicidalIntent: '자살의도',
  selfHarm: '자해',
  homicidalIdeation: '타해사고',
  violenceRisk: '폭력',
  abuseOrNeglect: '학대/방임',
  substanceRisk: '물질 사용',
  acutePsychiatricRisk: '급성 정신건강 위험',
};

const INACTIVE_GOAL = new Set(['achieved', 'paused', 'terminated']);

export const lines = (s?: string) =>
  (s ?? '')
    .split(/\r?\n/)
    .map((l) => l.replace(/^\s*[-•·*]\s*/, '').trim())
    .filter(Boolean);

export type BriefInput = {
  sourceSession: Session;
  journal: CounselingJournal;
  analysis?: SessionAnalysis;
  goals: CounselingGoal[];
  formulation?: CaseFormulationRevision;
  risk?: RiskAssessment;
  previousBrief?: NextSessionBrief;
  /** 반영된 AI 결과의 다음 회기 제안 (가설·질문·계획으로만 합친다) */
  ai?: AiBriefSuggestions;
};

export type AiBriefSuggestions = {
  sessionObjectives: string[];
  assessmentQuestions: { question: string; purpose?: string }[];
  hypothesesToTest: string[];
  riskFollowUp: string[];
  unfinishedTopics: string[];
  recommendedInterventions: {
    intervention: string;
    orientation?: string;
    rationale?: string;
    expectedGoal?: string;
    caution?: string;
  }[];
};

const norm = (t: string) => t.replace(/\s+/g, '').toLowerCase();
function mergeUnique(base: string[] | undefined, add: string[], tag = ''): string[] | undefined {
  const out = [...(base ?? [])];
  const seen = new Set(out.map(norm));
  for (const x of add) {
    const t = x.trim();
    if (t && !seen.has(norm(t))) {
      seen.add(norm(t));
      out.push(tag + t);
    }
  }
  return out.length ? out : undefined;
}

export type BriefContent = Pick<
  NextSessionBrief,
  | 'clientId'
  | 'sourceSessionId'
  | 'targetSessionNumber'
  | 'previousSessionSummary'
  | 'activeGoalIds'
  | 'recentChanges'
  | 'formulationSummary'
  | 'hypothesesToTest'
  | 'sessionObjectives'
  | 'assessmentQuestions'
  | 'interventionSuggestions'
  | 'riskFollowUp'
  | 'unfinishedTopics'
  | 'generatedBy'
  | 'status'
>;

export function riskFollowUps(risk?: RiskAssessment): string[] {
  if (!risk) return [];
  const out: string[] = [];
  for (const k of RISK_KEYS) {
    const item = risk[k];
    if (item.status === 'present' || item.status === 'unclear' || item.followUpRequired) {
      const state = item.status === 'present' ? '보고됨' : item.status === 'unclear' ? '불분명' : '추적 필요';
      out.push(
        `${RISK_LABELS[k]}: ${state}${item.actionTaken ? ` (조치: ${item.actionTaken})` : ''} — 이번 회기 재확인`,
      );
    }
  }
  return out;
}

function summarize(journal: CounselingJournal, analysis?: SessionAnalysis): string | undefined {
  const parts = [analysis?.sessionSummary, journal.importantIssue].filter((x): x is string => !!x?.trim());
  return parts.length ? parts.join('\n') : undefined;
}

export function buildBrief(input: BriefInput, newId: () => string): BriefContent {
  const { sourceSession, journal, analysis, formulation } = input;
  const rejected = new Set(formulation?.rejectedHypotheses ?? []);
  const hypotheses = formulation
    ? [
        ...(formulation.beliefHypotheses ?? []),
        ...(formulation.coreCognitiveThemes ?? []),
        ...(formulation.relationalPatterns ?? []),
        ...(formulation.behavioralPatterns ?? []),
      ]
        .filter((i) => i.status === 'hypothesis' && !rejected.has(i.id))
        .map((i) => i.text)
    : [];
  const plan = lines(journal.nextSessionPlan);
  const followUpInterventions = (analysis?.interventions ?? []).filter((i) => i.followUpNeeded);

  const content: BriefContent = {
    clientId: sourceSession.clientId,
    sourceSessionId: sourceSession.id,
    targetSessionNumber: sourceSession.sessionNumber + 1,
    previousSessionSummary: summarize(journal, analysis),
    activeGoalIds: input.goals.filter((g) => !g.deletedAt && !INACTIVE_GOAL.has(g.status)).map((g) => g.id),
    recentChanges: analysis?.notableChanges?.length ? analysis.notableChanges : undefined,
    formulationSummary: formulation?.currentProblems?.length
      ? `현재 주요 문제: ${formulation.currentProblems.join(', ')} (사례개념화 v${formulation.version})`
      : undefined,
    hypothesesToTest: hypotheses.length ? hypotheses : undefined,
    sessionObjectives: plan.length ? plan.slice(0, 2) : undefined,
    assessmentQuestions: formulation?.theoryFormulations
      ?.flatMap((t) => t.questionsToVerify ?? [])
      .slice(0, 5)
      .map((q) => ({ id: newId(), question: q, purpose: '사례개념화 확인' })),
    interventionSuggestions: followUpInterventions.length
      ? followUpInterventions.map((i) => ({
          id: newId(),
          intervention: i.interventionName,
          ...(i.theoreticalOrientation ? { orientation: i.theoreticalOrientation } : {}),
          rationale: '지난 회기 후속 필요로 표시됨',
        }))
      : undefined,
    riskFollowUp: riskFollowUps(input.risk),
    unfinishedTopics: plan.length > 2 ? plan.slice(2) : undefined,
    generatedBy: 'rule',
    status: 'draft',
  };
  const ai = input.ai;
  if (ai) {
    const qs = new Set((content.assessmentQuestions ?? []).map((q) => norm(q.question)));
    const ivs = new Set((content.interventionSuggestions ?? []).map((i) => norm(i.intervention)));
    const aiQs = ai.assessmentQuestions
      .filter((q) => q.question.trim() && !qs.has(norm(q.question)))
      .map((q) => ({ id: newId(), question: q.question.trim(), ...(q.purpose ? { purpose: q.purpose } : {}) }));
    const aiIvs = ai.recommendedInterventions
      .filter((i) => i.intervention.trim() && !ivs.has(norm(i.intervention)))
      .slice(0, 4)
      .map((i) => ({
        id: newId(),
        intervention: i.intervention.trim(),
        ...(i.orientation ? { orientation: i.orientation } : {}),
        ...(i.rationale ? { rationale: i.rationale } : {}),
        ...(i.expectedGoal ? { expectedGoal: i.expectedGoal } : {}),
        ...(i.caution ? { caution: i.caution } : {}),
      }));
    content.sessionObjectives = mergeUnique(content.sessionObjectives, ai.sessionObjectives);
    content.hypothesesToTest = mergeUnique(content.hypothesesToTest, ai.hypothesesToTest);
    content.riskFollowUp = mergeUnique(content.riskFollowUp, ai.riskFollowUp) ?? [];
    content.unfinishedTopics = mergeUnique(content.unfinishedTopics, ai.unfinishedTopics);
    if (aiQs.length) content.assessmentQuestions = [...(content.assessmentQuestions ?? []), ...aiQs].slice(0, 8);
    if (aiIvs.length) content.interventionSuggestions = [...(content.interventionSuggestions ?? []), ...aiIvs];
    if (aiQs.length || aiIvs.length || ai.sessionObjectives.length || ai.hypothesesToTest.length)
      content.generatedBy = 'ai';
  }
  // 빈 값은 저장하지 않는다 (자료 없음과 빈 목록을 구분)
  return Object.fromEntries(
    Object.entries(content).filter(([, v]) => v !== undefined && !(Array.isArray(v) && v.length === 0)),
  ) as BriefContent;
}
